import { Suspense, type ReactNode } from 'react'
import { lazyWithRetry } from '@/lib/lazyWithRetry'
import { RouteErrorFallback } from '@/components/RouteErrorFallback'
import { createBrowserRouter, Navigate, useLocation, useParams } from 'react-router-dom'
import { AppLayout } from '@/components/layout/AppLayout'
import { PostPropertyLayout } from '@/components/post-property/PostPropertyLayout'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { ToolPageLayout } from '@/components/layout/ToolPageLayout'
import { CourseWebViewLayout } from '@/components/layout/CourseWebViewLayout'
import { HomePage } from '@/pages/HomePage'
import { NotFoundPage } from '@/pages/NotFoundPage'
import { PATHS } from '@/routes/paths'
import { isSeoLandingPathname } from '@/lib/seoLandingPath'

// Split out of the entry bundle so the home page (most landings) parses less JS; PostProperty pulls leaflet.
const PropertiesPage = lazyWithRetry(() => import('@/pages/PropertiesPage').then((m) => ({ default: m.PropertiesPage })))
const PropertyDetailPage = lazyWithRetry(() => import('@/pages/PropertyDetailPage').then((m) => ({ default: m.PropertyDetailPage })))
const PostPropertyPage = lazyWithRetry(() => import('@/pages/PostPropertyPage').then((m) => ({ default: m.PostPropertyPage })))

const AboutPage = lazyWithRetry(() => import('@/pages/AboutPage').then((m) => ({ default: m.AboutPage })))
const ContactPage = lazyWithRetry(() => import('@/pages/ContactPage').then((m) => ({ default: m.ContactPage })))
const HomeLoanEmiPage = lazyWithRetry(() => import('@/pages/HomeLoanEmiPage').then((m) => ({ default: m.HomeLoanEmiPage })))
const HomeLoanEligibilityPage = lazyWithRetry(() =>
  import('@/pages/HomeLoanEligibilityPage').then((m) => ({ default: m.HomeLoanEligibilityPage })),
)
const BlogListPage = lazyWithRetry(() => import('@/pages/BlogListPage').then((m) => ({ default: m.BlogListPage })))
const BlogDetailPage = lazyWithRetry(() => import('@/pages/BlogDetailPage').then((m) => ({ default: m.BlogDetailPage })))
const MagazineListPage = lazyWithRetry(() => import('@/pages/MagazineListPage').then((m) => ({ default: m.MagazineListPage })))
const MagazineDetailPage = lazyWithRetry(() => import('@/pages/MagazineDetailPage').then((m) => ({ default: m.MagazineDetailPage })))
const MediaListPage = lazyWithRetry(() => import('@/pages/MediaListPage').then((m) => ({ default: m.MediaListPage })))
const MediaDetailPage = lazyWithRetry(() => import('@/pages/MediaDetailPage').then((m) => ({ default: m.MediaDetailPage })))
const FAQPage = lazyWithRetry(() => import('@/pages/FAQPage').then((m) => ({ default: m.FAQPage })))
const PremiumProductsPage = lazyWithRetry(() => import('@/pages/PremiumProductsPage').then((m) => ({ default: m.PremiumProductsPage })))
const PremiumProductDetailPage = lazyWithRetry(() => import('@/pages/PremiumProductDetailPage').then((m) => ({ default: m.PremiumProductDetailPage })))
const AchievementsPage = lazyWithRetry(() => import('@/pages/AchievementsPage').then((m) => ({ default: m.AchievementsPage })))
const FeedbackPage = lazyWithRetry(() => import('@/pages/FeedbackPage').then((m) => ({ default: m.FeedbackPage })))
const LegalPage = lazyWithRetry(() => import('@/pages/LegalPage').then((m) => ({ default: m.LegalPage })))
const DashboardPage = lazyWithRetry(() => import('@/pages/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })))
const ProfilePage = lazyWithRetry(() => import('@/pages/dashboard/ProfilePage').then((m) => ({ default: m.ProfilePage })))
const MyPropertiesPage = lazyWithRetry(() => import('@/pages/dashboard/MyPropertiesPage').then((m) => ({ default: m.MyPropertiesPage })))
const MyInquiriesPage = lazyWithRetry(() => import('@/pages/dashboard/MyInquiriesPage').then((m) => ({ default: m.MyInquiriesPage })))
const MySiteVisitsPage = lazyWithRetry(() => import('@/pages/dashboard/MySiteVisitsPage').then((m) => ({ default: m.MySiteVisitsPage })))
const LikedPropertiesPage = lazyWithRetry(() => import('@/pages/dashboard/LikedPropertiesPage').then((m) => ({ default: m.LikedPropertiesPage })))
const MyOrdersPage = lazyWithRetry(() => import('@/pages/dashboard/MyOrdersPage').then((m) => ({ default: m.MyOrdersPage })))
const BlogCategoryPage = lazyWithRetry(() => import('@/pages/BlogCategoryPage').then((m) => ({ default: m.BlogCategoryPage })))
const PremiumPaymentResultPage = lazyWithRetry(() => import('@/pages/PremiumPaymentResultPage').then((m) => ({ default: m.PremiumPaymentResultPage })))
const CostCalculatorPage = lazyWithRetry(() => import('@/pages/CostCalculatorPage').then((m) => ({ default: m.CostCalculatorPage })))
const RealEstateBookPage = lazyWithRetry(() => import('@/pages/RealEstateBookPage').then((m) => ({ default: m.RealEstateBookPage })))
const PremiumBenefitsPage = lazyWithRetry(() => import('@/pages/PremiumBenefitsPage').then((m) => ({ default: m.PremiumBenefitsPage })))
const PremiumBenefitPage = lazyWithRetry(() => import('@/pages/courses/PremiumBenefitPage').then((m) => ({ default: m.PremiumBenefitPage })))
const PremiumCourseDetailPage = lazyWithRetry(() => import('@/pages/courses/PremiumCourseDetailPage').then((m) => ({ default: m.PremiumCourseDetailPage })))
const CoursePaymentDetailsPage = lazyWithRetry(() => import('@/pages/courses/CoursePaymentDetailsPage').then((m) => ({ default: m.CoursePaymentDetailsPage })))
const LearnOverviewPage = lazyWithRetry(() => import('@/pages/courses/LearnOverviewPage').then((m) => ({ default: m.LearnOverviewPage })))
const CourseVideoPage = lazyWithRetry(() => import('@/pages/courses/CourseVideoPage').then((m) => ({ default: m.CourseVideoPage })))
const CoursePurchaseHistoryPage = lazyWithRetry(() => import('@/pages/courses/CoursePurchaseHistoryPage').then((m) => ({ default: m.CoursePurchaseHistoryPage })))

function PageLoader() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
    </div>
  )
}

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>
}

function LegacyRedirect({ to }: { to: string }) {
  const { search } = useLocation()
  return <Navigate to={`${to}${search}`} replace />
}

/** Old listing URLs → /properties, mapping category/keyword path segments into query params. */
function PropertyListLegacyRedirect() {
  const { pathname, search } = useLocation()
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)

  const categoryMatch = pathname.match(/\/propertylist\/category\/([^/]+)/i)
  const keywordMatch = pathname.match(/\/propertylist\/keyword\/([^/]+)/i)
  const listingTail = pathname.match(/\/propertylisting\/([^/]+)/i)
  const listTail = pathname.match(/\/propertylist\/([^/]+)/i)

  if (categoryMatch && !params.get('category')) {
    params.set('category', decodeURIComponent(categoryMatch[1]).replace(/-/g, ' '))
  } else if (keywordMatch && !params.get('search')) {
    params.set('search', decodeURIComponent(keywordMatch[1]).replace(/-/g, ' '))
  } else {
    const reserved = new Set(['category', 'keyword'])
    const tail =
      listingTail?.[1] ??
      (listTail?.[1] && !reserved.has(listTail[1].toLowerCase()) ? listTail[1] : null)
    if (tail && !params.get('search')) {
      params.set('search', decodeURIComponent(tail).replace(/-/g, ' '))
    }
  }

  const qs = params.toString()
  return <Navigate to={`${PATHS.properties}${qs ? `?${qs}` : ''}`} replace />
}

function EditPropertyRedirect() {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={`/edit-property/${id ?? ''}`} replace />
}

function PremiumProductAltRedirect() {
  const { id } = useParams<{ id: string }>()
  const { search } = useLocation()
  return <Navigate to={`${PATHS.premiumProduct(id ?? '')}${search}`} replace />
}

function BlogDetailAltRedirect() {
  const { slug } = useParams<{ slug: string }>()
  const { search } = useLocation()
  return <Navigate to={`${PATHS.blogDetail(slug ?? '')}${search}`} replace />
}

function BlogCategoryAltRedirect() {
  const { slug } = useParams<{ slug: string }>()
  const { search } = useLocation()
  return <Navigate to={`${PATHS.blogCategory(slug ?? '')}${search}`} replace />
}

/** Unknown routes: Phase-1 SEO landings → listing UI; everything else → 404. */
function SeoLandingOrNotFound() {
  const { pathname } = useLocation()
  if (isSeoLandingPathname(pathname)) {
    return <Lazy><PropertiesPage /></Lazy>
  }
  return <NotFoundPage />
}

export const router = createBrowserRouter([
  {
    element: <PostPropertyLayout />,
    errorElement: <RouteErrorFallback />,
    children: [
      { path: '/add-property', element: <Lazy><PostPropertyPage /></Lazy> },
      { path: '/edit-property/:id', element: <Lazy><PostPropertyPage /></Lazy> },
      { path: '/addproperty', element: <LegacyRedirect to={PATHS.addProperty} /> },
      { path: '/post-property', element: <LegacyRedirect to={PATHS.addProperty} /> },
    ],
  },
  {
    element: <CourseWebViewLayout />,
    errorElement: <RouteErrorFallback />,
    children: [
      { path: '/premium_benefit', element: <Lazy><PremiumBenefitPage /></Lazy> },
      { path: '/premiumcourselist', element: <Lazy><PremiumCourseDetailPage /></Lazy> },
      { path: '/learnoverview', element: <Lazy><LearnOverviewPage /></Lazy> },
      { path: '/video/:id', element: <Lazy><CourseVideoPage /></Lazy> },
      { path: '/paymentdetails', element: <Lazy><CoursePaymentDetailsPage /></Lazy> },
      { path: '/purchase_history', element: <Lazy><CoursePurchaseHistoryPage /></Lazy> },
    ],
  },
  {
    element: <ToolPageLayout />,
    errorElement: <RouteErrorFallback />,
    children: [
      { path: '/calculator', element: <Lazy><CostCalculatorPage /></Lazy> },
      { path: '/realestate-book', element: <Lazy><RealEstateBookPage /></Lazy> },
      // Mobile app WebView — typo path preserved; products grid (not marketing landing)
      { path: '/premium_benifits', element: <Lazy><PremiumBenefitsPage /></Lazy> },
    ],
  },
  {
    path: '/',
    element: <AppLayout />,
    errorElement: <RouteErrorFallback />,
    children: [
      { index: true, element: <HomePage /> },

      // Property — one canonical browse URL
      { path: 'properties', element: <Lazy><PropertiesPage /></Lazy> },
      { path: 'propertylist', element: <PropertyListLegacyRedirect /> },
      { path: 'propertylist/*', element: <PropertyListLegacyRedirect /> },
      { path: 'propertylisting', element: <PropertyListLegacyRedirect /> },
      { path: 'propertylisting/*', element: <PropertyListLegacyRedirect /> },
      { path: 'property/:slug', element: <Lazy><PropertyDetailPage /></Lazy> },

      { path: 'editproperty/:id', element: <EditPropertyRedirect /> },

      // User account area
      {
        path: '',
        element: <DashboardLayout />,
        children: [
          { path: 'dashboard', element: <Lazy><DashboardPage /></Lazy> },
          { path: 'user_dashboard', element: <LegacyRedirect to={PATHS.dashboard} /> },
          { path: 'editprofile', element: <Lazy><ProfilePage /></Lazy> },
          { path: 'profile', element: <LegacyRedirect to={PATHS.editProfile} /> },
          { path: 'saved-properties', element: <Lazy><LikedPropertiesPage /></Lazy> },
          { path: 'my-wishlist', element: <LegacyRedirect to={PATHS.myWishlist} /> },
          { path: 'my-property', element: <Lazy><MyPropertiesPage /></Lazy> },
          { path: 'myproperty', element: <LegacyRedirect to={PATHS.myProperty} /> },
          { path: 'my-inquiries', element: <Lazy><MyInquiriesPage /></Lazy> },
          { path: 'myinquiery', element: <LegacyRedirect to={PATHS.myInquiry} /> },
          { path: 'my-leads', element: <LegacyRedirect to={PATHS.myInquiry} /> },
          { path: 'my-site-visits', element: <Lazy><MySiteVisitsPage /></Lazy> },
          { path: 'my-orders', element: <Lazy><MyOrdersPage /></Lazy> },
          { path: 'my-payments', element: <LegacyRedirect to={PATHS.myOrders} /> },
          { path: 'mysitevisit', element: <LegacyRedirect to={PATHS.mySiteVisit} /> },
          { path: 'sitevisit', element: <LegacyRedirect to={PATHS.mySiteVisit} /> },
          { path: 'sitevisiterdetail', element: <LegacyRedirect to={PATHS.mySiteVisit} /> },
        ],
      },

      // Blog
      { path: 'blog', element: <Lazy><BlogListPage /></Lazy> },
      { path: 'blog/category/:slug', element: <Lazy><BlogCategoryPage /></Lazy> },
      { path: 'blogcategory/:slug', element: <Lazy><BlogCategoryPage /></Lazy> },
      { path: 'blog/:slug', element: <Lazy><BlogDetailPage /></Lazy> },
      { path: 'blogs', element: <LegacyRedirect to={PATHS.blog} /> },
      { path: 'blogs/category/:slug', element: <BlogCategoryAltRedirect /> },
      { path: 'blogs/:slug', element: <BlogDetailAltRedirect /> },

      // Magazine
      { path: 'magazine', element: <Lazy><MagazineListPage /></Lazy> },
      { path: 'magazine/:slug', element: <Lazy><MagazineDetailPage /></Lazy> },

      // Media Center
      { path: 'media', element: <Lazy><MediaListPage /></Lazy> },
      { path: 'media/:slug', element: <Lazy><MediaDetailPage /></Lazy> },

      // Legacy digital course URLs (mobile WebView deep links — handled above in CourseWebViewLayout)
      { path: 'coursedetail', element: <LegacyRedirect to={PATHS.premiumCourseList} /> },
      { path: 'invoice/:id', element: <LegacyRedirect to={PATHS.premium} /> },

      // Premium payment callbacks
      { path: 'premium-package/payment-success/:transaction', element: <Lazy><PremiumPaymentResultPage type="success" /></Lazy> },
      { path: 'premium-package/payment-failure', element: <Lazy><PremiumPaymentResultPage type="failure" /></Lazy> },
      { path: 'premium-package/payment-failure/:transaction', element: <Lazy><PremiumPaymentResultPage type="failure" /></Lazy> },

      // Company pages — canonical = old Fronted URLs; short/new paths redirect
      { path: 'about-us', element: <Lazy><AboutPage /></Lazy> },
      { path: 'about', element: <LegacyRedirect to={PATHS.about} /> },
      { path: 'aboutus', element: <LegacyRedirect to={PATHS.about} /> },
      { path: 'contact-us', element: <Lazy><ContactPage /></Lazy> },
      { path: 'contact', element: <LegacyRedirect to={PATHS.contact} /> },
      { path: 'home-loan-emi', element: <Lazy><HomeLoanEmiPage /></Lazy> },
      { path: 'home-loan-eligibility', element: <Lazy><HomeLoanEligibilityPage /></Lazy> },
      { path: 'achievements', element: <Lazy><AchievementsPage /></Lazy> },
      { path: 'achievement', element: <LegacyRedirect to={PATHS.achievements} /> },
      { path: 'feedback', element: <Lazy><FeedbackPage /></Lazy> },
      { path: 'faq', element: <Lazy><FAQPage /></Lazy> },
      { path: 'ourproducts', element: <Lazy><PremiumProductsPage /></Lazy> },
      { path: 'ourproducts/:id', element: <Lazy><PremiumProductDetailPage /></Lazy> },
      { path: 'our-products', element: <LegacyRedirect to={PATHS.premium} /> },
      { path: 'our-products/:id', element: <PremiumProductAltRedirect /> },

      // Legal — canonical = old Fronted URLs; short/new paths redirect
      { path: 'privacy-policy', element: <Lazy><LegalPage type="privacy" /></Lazy> },
      { path: 'privacy-and-policy', element: <LegacyRedirect to={PATHS.privacy} /> },
      { path: 'terms-and-conditions', element: <Lazy><LegalPage type="terms" /></Lazy> },
      { path: 'terms', element: <LegacyRedirect to={PATHS.terms} /> },
      { path: 'terms-and-condition', element: <LegacyRedirect to={PATHS.terms} /> },
      { path: 'termsandcondtion', element: <LegacyRedirect to={PATHS.terms} /> },
      { path: 'sitemap', element: <LegacyRedirect to={PATHS.home} /> },
      { path: 'sitemap.html', element: <LegacyRedirect to={PATHS.home} /> },

      { path: '404', element: <NotFoundPage /> },
      { path: '*', element: <SeoLandingOrNotFound /> },
    ],
  },
])
